/**
 * Shared safe-area and FAB layout utilities for PWA/mobile.
 * Use with CSS vars defined in :root (tailwind.css):
 *   --sat, --sab, --sal, --sar (safe-area insets)
 *   --fab-size, --fab-margin, --fab-bottom (FAB position)
 *   --page-bottom-clearance (scrollable page bottom padding)
 *   --keyboard (visualViewport-based, set by keyboard observer)
 */

import type { CSSProperties } from 'react';

/** FAB size in px (matches AppShell chat FAB). */
export const FAB_SIZE_PX = 56;

/** FAB bottom offset (margin above safe-area). */
export const FAB_MARGIN_PX = 28;

/**
 * Total vertical space reserved for FAB: size + margin + safe-area.
 * Use this as min bottom padding on scrollable pages so content is not hidden behind FAB.
 */
export const FAB_CLEARANCE_PX = FAB_SIZE_PX + FAB_MARGIN_PX;

/** Inline style type for layout helpers. */
export type LayoutStyle = CSSProperties;

/**
 * Inline style for fixed FAB position (bottom).
 * Uses CSS var --fab-bottom when available so one source of truth.
 */
export function getFabBottomStyle(): LayoutStyle {
  return {
    bottom: 'var(--fab-bottom, calc(28px + env(safe-area-inset-bottom, 0px)))',
    width: `${FAB_SIZE_PX}px`,
    height: `${FAB_SIZE_PX}px`,
  };
}

/**
 * Inline style for scrollable page bottom padding so content clears FAB + safe-area.
 * Use on the main scroll container of pages that show the global chat FAB.
 */
export function getPageBottomClearanceStyle(): LayoutStyle {
  return {
    paddingBottom: 'var(--page-bottom-clearance, calc(84px + env(safe-area-inset-bottom, 0px)))',
  };
}

/**
 * Bottom padding that includes keyboard height (for bottom-fixed composers/sheets).
 * Use var(--keyboard) so visualViewport-based value from keyboard observer is applied.
 */
export function getComposerBottomPaddingStyle(extraPx = 12): LayoutStyle {
  return {
    paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + var(--keyboard, 0px) + ${extraPx}px)`,
  };
}
